/**
 * Portal section manifest — the single source of truth for WHICH sections exist in
 * the client portal, WHO they belong to (person type) and HOW they validate.
 *
 * This is the framework-free core of the "data dictionary": no React, no Nest, no
 * Mongoose. Every consumer projects from here:
 *  - API `submit-complete` iterates `activeSections()` instead of copy-pasting an
 *    `if (enabled.includes(X)) X.safeParse()` block per section.
 *  - Web wizard / review derive their steps from the same list.
 *  - The persistence layer (API) binds each id to its sandbox key separately
 *    (`entityKey` is a persistence concern and lives in the API registry, NOT here).
 *
 * Adding a section (or a person type) = add ONE spec here + its Zod schema. The
 * iterating consumers do not change. The companion `fields.ts` holds the per-field
 * metadata (labels, review formatters, document-engine variables).
 */
import type { ZodTypeAny } from 'zod'
import { EconomicDeclaration, EconomicDeclarationJuridical } from '../entities/client/economic'
import { EquityDeclaration, EquityDeclarationJuridical } from '../entities/client/equity'
import { IndividualApplicant } from '../entities/client/individual'
import {
  LegalRepresentativeDeclaration,
  LegalRepresentativeDeclarationIndividual
} from '../entities/client/legal-representatives'
import { PepDeclaration } from '../entities/client/pep'
import type { ProgressiveLevel } from '../utils/progressive'
import { CompanyApplicant } from './juridical/company'

/** Person type the portal flow identifies. */
export type PortalClientType = 'individual' | 'juridical'

/**
 * Canonical section ids. This is the SINGLE source — the API `PortalFormSection`
 * enum mirrors these exact string values (kept in sync by a compile-time check on
 * the API side). Use the string-union here so the manifest stays framework-free.
 */
export type PortalSectionId =
  | 'company' // identidad de la persona jurídica (ancla de un flujo juridical)
  | 'applicant'
  | 'contact' // ⚠️ NO es una sección de datos — ver la nota de abajo
  | 'pep'
  | 'economic'
  | 'equity'
  | 'legal_representatives'
  | 'attachments'

/**
 * ⚠️ `'contact'` es un id de PRESENTACIÓN, no una sección de datos, y por eso no aparece
 * en `PORTAL_SECTIONS` (8 ids acá, 7 specs allá — la diferencia es deliberada).
 *
 * Sus campos (correo, teléfonos) viven dentro del sandbox de `applicant` y validan con su
 * schema. Existe como id porque DOS consumidores lo necesitan como etiqueta:
 *  - el wizard web lo usa como paso propio (`form/section-registry.tsx`), y
 *  - una corrección puede apuntar a él (`fieldReviews[].section === 'contact'`).
 *
 * Consecuencia práctica: `getSectionFields('contact')` devuelve `undefined`, igual que
 * `attachments`. Quien itere el diccionario tiene que tolerarlo — no es un bug, y si lo
 * "arreglás" agregándole un catálogo vas a duplicar los campos de contacto del solicitante.
 */

/**
 * Runtime context a flow provides to resolve validation/inclusion. Everything the
 * manifest needs to decide "does this section apply, and how does it validate?".
 */
export interface SectionContext {
  clientType: PortalClientType
  /** Progressive level of the expedient (drives applicant's required fields). */
  level: ProgressiveLevel
  /** Sections enabled for this flow (empty = all, as a safe fallback). */
  enabledSections: PortalSectionId[]
  /** Submitted applicant sandbox — read for conditional rules (e.g. `ownAct`). */
  applicant?: Record<string, unknown> | null
  /** ObjectId of the "Other" profession (makes `otherProfession` required). */
  otherProfessionId?: string
}

/**
 * Describes one section's identity + validation contract. UI is intentionally NOT
 * here: the form layout stays hand-built (design flexibility). This owns the DATA.
 */
export interface PortalSectionSpec {
  id: PortalSectionId
  /** Spanish label for emails / notifications. */
  label: string
  /** Person types this section belongs to. */
  clientTypes: readonly PortalClientType[]
  /**
   * Whether the section is validated by a Zod schema at submit. `attachments` is
   * `false` — it is validated by the documents subsystem (magic bytes, requirements).
   */
  validatesViaSchema: boolean
  /** Static validation schema (pep / economic / equity / legal_representatives). */
  schema?: ZodTypeAny
  /** Level-resolved schema (applicant). Takes precedence over `schema` when present. */
  resolveSchema?: (ctx: SectionContext) => ZodTypeAny
  /**
   * Safety net applied before persisting/validating at submit: drops data a "No"
   * flag should not keep (e.g. cleared PEP arrays). Pure — returns a new object.
   * Mirrors exactly what `submit-complete` did inline per section.
   */
  normalizeForSubmit?: (
    data: Record<string, unknown>,
    ctx: SectionContext
  ) => Record<string, unknown>
  /**
   * Does this section participate in the given flow (shown, saved, validated)?
   * Default rule: belongs to the client type AND (no enabled list → all, else listed).
   * Overridden for conditional sections (legal representative, attachments).
   */
  isIncluded: (ctx: SectionContext) => boolean
  /**
   * Per-person-type step order override (lower = earlier). Defaults to manifest array
   * position. Needed where the order differs by person type — e.g. juridical shows
   * Estado Patrimonial before Información económica, individual the reverse.
   */
  order?: Partial<Record<PortalClientType, number>>
}

/**
 * Whether THIS flow collects the legal representative section.
 *  - Juridical: it legally always has one, but the flow decides whether to (re)collect it
 *    → enabled-gated, so an admin can skip it (e.g. an equity-only flow of a client whose
 *    representative is already on file). Empty enabled list keeps it (safe fallback).
 *  - Individual: driven by the titular's runtime declaration (`ownAct === false`), NOT by
 *    the admin gate — you cannot pre-know at creation time whether they act through one.
 */
function collectsLegalRepresentative(ctx: SectionContext): boolean {
  if (ctx.clientType === 'juridical') return enabledGate('legal_representatives', ctx)
  return ctx.applicant?.ownAct === false
}

/** Default inclusion: matches client type and the enabled-sections gate. */
function enabledGate(id: PortalSectionId, ctx: SectionContext): boolean {
  return ctx.enabledSections.length === 0 || ctx.enabledSections.includes(id)
}

/** Clears array fields whose boolean flag is falsy (PEP/economic "No" → empty). */
function clearWhenFalse(
  data: Record<string, unknown>,
  pairs: ReadonlyArray<[flag: string, field: string]>
): Record<string, unknown> {
  const next = { ...data }
  for (const [flag, field] of pairs) {
    if (!next[flag]) next[field] = []
  }
  return next
}

export const PORTAL_SECTIONS: readonly PortalSectionSpec[] = [
  {
    id: 'company',
    label: 'Datos de la empresa',
    clientTypes: ['juridical'],
    validatesViaSchema: true,
    resolveSchema: (ctx) => CompanyApplicant.forLevel(ctx.level),
    // Identity anchor of a juridical flow, but still enabled-gated: a flow that does not
    // list `company` (e.g. an equity-only revalidation of an already-known client) skips
    // the company step. Empty enabled list keeps it (safe fallback via `enabledGate`).
    isIncluded: (ctx) => ctx.clientType === 'juridical' && enabledGate('company', ctx)
  },
  {
    id: 'applicant',
    label: 'Datos personales',
    clientTypes: ['individual'],
    validatesViaSchema: true,
    resolveSchema: (ctx) =>
      IndividualApplicant.forLevel(
        ctx.level,
        ctx.otherProfessionId ? { otherProfessionId: ctx.otherProfessionId } : undefined
      ),
    // Identity anchor of an individual flow, but still enabled-gated: a flow that does not
    // list `applicant` (e.g. an equity-only revalidation of an already-known client) skips
    // the personal-data steps. Empty enabled list keeps it (safe fallback via `enabledGate`).
    isIncluded: (ctx) => ctx.clientType === 'individual' && enabledGate('applicant', ctx)
  },
  {
    id: 'legal_representatives',
    label: 'Representantes legales',
    clientTypes: ['individual', 'juridical'],
    validatesViaSchema: true,
    // Juridical requires the appointment register (Registro Mercantil); the individual mandate
    // is not filed there, so its schema does not require `powerRegister`.
    resolveSchema: (ctx) =>
      ctx.clientType === 'juridical'
        ? LegalRepresentativeDeclaration
        : LegalRepresentativeDeclarationIndividual,
    // Juridical: enabled-gated (the flow may skip re-collecting the rep). Individual: driven
    // by `ownAct`. Placed right after the identity section so its wizard step lands at #3.
    normalizeForSubmit: (data, ctx) => {
      if (collectsLegalRepresentative(ctx)) return { ...data }
      // Juridical that does NOT (re)collect the rep: leave the sandbox UNTOUCHED. Clearing
      // to [] would make the sync write an empty array and WIPE the client's existing reps
      // on approve. The sync boundary skips a non-collected section, so stale data is inert.
      if (ctx.clientType === 'juridical') return { ...data }
      // Individual acting on own behalf: the declaration says there is no rep → drop it.
      return { ...data, representatives: [] }
    },
    isIncluded: (ctx) => collectsLegalRepresentative(ctx)
  },
  {
    id: 'pep',
    label: 'Declaración PEP',
    clientTypes: ['individual'],
    validatesViaSchema: true,
    schema: PepDeclaration,
    normalizeForSubmit: (data) =>
      clearWhenFalse(data, [
        ['isPep', 'pep'],
        ['hasRelativesPep', 'relativesPep'],
        ['hasPartnersPep', 'partnersPep']
      ]),
    isIncluded: (ctx) => ctx.clientType === 'individual' && enabledGate('pep', ctx)
  },
  {
    id: 'economic',
    label: 'Información económica',
    clientTypes: ['individual', 'juridical'],
    validatesViaSchema: true,
    // Juridical's economic info is a DIFFERENT model (entity-level: relación de negocios,
    // empleados, proveedores, clientes, fuentes de fondos) — not income sources of a person.
    resolveSchema: (ctx) =>
      ctx.clientType === 'juridical' ? EconomicDeclarationJuridical : EconomicDeclaration,
    normalizeForSubmit: (data, ctx) =>
      // Juridical has no boolean "income source" flags → nothing to clear.
      ctx.clientType === 'juridical'
        ? { ...data }
        : clearWhenFalse(data, [
            ['hasDependencyRelation', 'dependencyRelations'],
            ['hasOwnBusiness', 'ownBusinesses'],
            ['hasOtherIncome', 'otherIncomes']
          ]),
    isIncluded: (ctx) => enabledGate('economic', ctx),
    // Juridical FEIC shows Información económica AFTER Estado Patrimonial.
    order: { juridical: 100 }
  },
  {
    id: 'equity',
    label: 'Estado patrimonial',
    clientTypes: ['individual', 'juridical'],
    validatesViaSchema: true,
    // Juridical's equity is the same personal balance MINUS spouse (a company has none —
    // mirrors the internal FEIC, where the spouse block is disabled). Individual keeps it.
    resolveSchema: (ctx) =>
      ctx.clientType === 'juridical' ? EquityDeclarationJuridical : EquityDeclaration,
    normalizeForSubmit: (data, ctx) => {
      // Juridical has no spouse fields → nothing to strip.
      if (ctx.clientType === 'juridical') return { ...data }
      // Individual: if no spouse declared, its data is not kept on submit.
      if (data.hasSpouse) return { ...data }
      return { ...data, spouse: {}, spouseIncludedInThePurchase: false }
    },
    isIncluded: (ctx) => enabledGate('equity', ctx),
    // Juridical FEIC shows Estado Patrimonial BEFORE Información económica.
    order: { juridical: 99 }
  },
  {
    id: 'attachments',
    label: 'Documentos adjuntos',
    clientTypes: ['individual', 'juridical'],
    // Validated by the documents subsystem (magic bytes + requirements), not by Zod.
    validatesViaSchema: false,
    // Visibility depends on whether the expedient has document requirements, which is
    // a runtime concern: the web wizard layers it (showAttachments). The submit loop
    // never schema-validates this section, so manifest inclusion stays conservative.
    isIncluded: (ctx) => enabledGate('attachments', ctx)
  }
]

const SECTION_BY_ID = new Map<PortalSectionId, PortalSectionSpec>(
  PORTAL_SECTIONS.map((s) => [s.id, s])
)

/** Spec by id (throws on an unknown id — ids come from the manifest, so this is a guard). */
export function getSection(id: PortalSectionId): PortalSectionSpec {
  const spec = SECTION_BY_ID.get(id)
  if (!spec) throw new Error(`Unknown portal section: ${id}`)
  return spec
}

/** All sections a given person type can ever have (ignores the per-flow gate). */
export function sectionsForClientType(clientType: PortalClientType): PortalSectionSpec[] {
  return PORTAL_SECTIONS.filter((s) => s.clientTypes.includes(clientType))
}

/**
 * Sections that participate in THIS flow (client type + enabled + conditional rules),
 * ordered by the per-person-type `order` override (falling back to manifest position).
 */
export function activeSections(ctx: SectionContext): PortalSectionSpec[] {
  const included = PORTAL_SECTIONS.map((spec, position) => ({ spec, position })).filter(
    ({ spec }) => spec.clientTypes.includes(ctx.clientType) && spec.isIncluded(ctx)
  )
  const rank = ({ spec, position }: { spec: PortalSectionSpec; position: number }): number =>
    spec.order?.[ctx.clientType] ?? position
  return included.sort((a, b) => rank(a) - rank(b)).map(({ spec }) => spec)
}

/**
 * Default enabled sections for a new flow of the given person type (the "MVP" set).
 * Conditional sections (legal representative, attachments) are excluded — they are
 * driven by declarations/requirements, not by the enabled list.
 */
export function mvpSectionsFor(clientType: PortalClientType): PortalSectionId[] {
  // Full-validation default (no flow chosen): every section of the type. Both
  // `legal_representatives` and `attachments` are real toggles now and default ON here;
  // for individual the rep is inert (its inclusion ignores the gate and follows `ownAct`).
  // The documents STEP still only renders when the expedient actually has requirements.
  return sectionsForClientType(clientType).map((s) => s.id)
}

/** Spanish label for a section id (fallback to the id). */
export function portalSectionLabel(id: PortalSectionId): string {
  return SECTION_BY_ID.get(id)?.label ?? id
}
